import { useCallback, useEffect, useRef } from 'react';
import { useEmailDraftCache } from 'hooks/canmail/useEmailDraftCache';
import type { EmailDraft } from '../types';

const AUTO_SAVE_INTERVAL_MS = 10_000;

interface UseEmailDraftOptions {
  /** Current draftable composer state. Read by the interval, never during render. */
  values: EmailDraft;
  /** Auto-save is suspended while editing an already-scheduled email. */
  enabled: boolean;
}

export interface UseEmailDraftResult {
  loadDraft: () => EmailDraft | null;
  clearDraft: () => void;
}

/**
 * Auto-saves the composer to localStorage on an interval and exposes restore/clear.
 *
 * The values are mirrored into a ref **inside an effect**, not during render. The
 * previous implementation assigned `draftValuesRef.current = {...}` in the render body,
 * which is a side effect in the render path — it happens to work today but breaks
 * under concurrent rendering, where a render can be started and thrown away.
 */
export function useEmailDraft({ values, enabled }: UseEmailDraftOptions): UseEmailDraftResult {
  const { saveDraft, loadDraft, clearDraft } = useEmailDraftCache();

  const valuesRef = useRef<EmailDraft>(values);
  useEffect(() => {
    valuesRef.current = values;
  }, [values]);

  useEffect(() => {
    if (!enabled) return undefined;
    const id = setInterval(() => {
      saveDraft(valuesRef.current);
    }, AUTO_SAVE_INTERVAL_MS);
    return () => clearInterval(id);
  }, [enabled, saveDraft]);

  return {
    loadDraft: useCallback(() => loadDraft(), [loadDraft]),
    clearDraft: useCallback(() => clearDraft(), [clearDraft]),
  };
}

export default useEmailDraft;
